import * as THREE from 'three';

import { MeasuredView } from './camera-angles';

/**
 * The parts of a measured camera that `lookAt` cannot carry.
 *
 * `lookAt` sets a direction and re-levels everything else, so two of the three
 * things a reference photograph tells us fall off the pose:
 *
 *   roll — a handheld reference leans, and lookAt always stands it back up.
 *   view — the field of view arrives in millimetres, and millimetres are not
 *          a field of view until you say what film they are on. The
 *          measurement uses a 24mm frame height; three.js's setFocalLength
 *          uses filmGauge / max(aspect, 1). On the portrait references this
 *          pipeline is given, those two disagree by 12 to 21 degrees.
 *
 * So the field of view is set as an angle, and the roll is put back by hand.
 * Lives apart from camera-angles.ts, which is deliberately three-free.
 */
export function applyMeasuredView(cam: THREE.PerspectiveCamera, view: MeasuredView): void {
    if (view.fovDegV && view.fovDegV > 5 && view.fovDegV < 140) {
        // Straight through as an angle: no film, no gauge, nothing to disagree
        // about. Assumes the studio renders at the reference's aspect ratio,
        // which is what the capture is set to.
        cam.fov = view.fovDegV;
        cam.updateProjectionMatrix();
    }
    if (view.rollDeg) {
        // About the view axis, after the pose — lookAt has just levelled it.
        // Negated: the measurement describes how the WORLD leans in the frame,
        // and the camera has to lean the other way to produce that.
        cam.rotateZ(THREE.MathUtils.degToRad(-view.rollDeg));
    }
}

/**
 * The lens value three.js needs in order to show a given field of view.
 *
 * Exactly `PerspectiveCamera.setFocalLength` inverted, on purpose. The studio
 * drives its camera through a lens control, so a measured field of view has to
 * arrive as a lens or the control will overwrite it on the next render. Being
 * the inverse of the function that consumes it, this round-trips exactly —
 * which is the one property a conversion between two conventions has to have,
 * and the one the millimetre path did not.
 */
export function lensForFov(fovDegV: number, aspect: number): number {
    const filmHeight = 35 / Math.max(aspect, 1);
    return (filmHeight / 2) / Math.tan(THREE.MathUtils.degToRad(fovDegV) / 2);
}

/**
 * The studio aspect closest to a reference's own shape.
 *
 * The studio shoots at one of a handful of preset ratios, chosen by the
 * recipe. When a measurement is being matched, that choice belongs to the
 * reference instead: a vertical field of view reproduces a framing only on a
 * frame of the same shape, and 67 degrees on a square canvas shows half a room
 * more than 67 degrees on a portrait one.
 *
 * Nearest in log space, so 16:9 and 9:16 are the same distance from square
 * rather than the landscape one always winning.
 */
export function nearestAspectPreset(
    ratio: number,
    presets: Array<{ v: string }>,
): string | null {
    if (!(ratio > 0) || !presets.length) return null;
    let best: string | null = null;
    let bestErr = Infinity;
    for (const p of presets) {
        const [w, h] = p.v.split(':').map(Number);
        if (!(w > 0) || !(h > 0)) continue;
        const err = Math.abs(Math.log(w / h) - Math.log(ratio));
        if (err < bestErr) {
            bestErr = err;
            best = p.v;
        }
    }
    return best;
}
